import { useState, type PointerEvent as ReactPointerEvent, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent } from 'react'
import { Box, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material'
import { HANDLE_WIDTH } from './useResizablePanel'

/**
 * Drag handle between the chat and surface panes.
 *
 * Dragging is held with setPointerCapture in useResizablePanel (see the notes
 * there), and this component adds the two things that make the control
 * forgiving rather than fiddly:
 *   - a 16px hit zone with a grip that grows on hover, so you don't have to
 *     land on a hairline;
 *   - a CLICK (a press that didn't move) opens preset splits, so setting the
 *     layout never depends on a precise drag at all. Arrow keys work too.
 * While dragging it drops a transparent full-screen overlay above the surface
 * iframe — capture already keeps the events flowing, this just stops the iframe
 * reacting to a pointer that isn't talking to it.
 */
export default function ResizeHandle({
  onPointerDown, onKeyDown, wasDrag, dragging, valueNow, valueMin, valueMax, presets, onPreset,
}: {
  onPointerDown: (e: ReactPointerEvent) => void
  onKeyDown: (e: ReactKeyboardEvent) => void
  wasDrag: () => boolean
  dragging: boolean
  valueNow: number
  valueMin: number
  valueMax: number
  presets: Array<{ label: string; width: number }>
  onPreset: (w: number) => void
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  // A captured drag ends with a synthetic click on this element, so only a press
  // that never moved counts as "clicked the handle".
  function onClick(e: ReactMouseEvent<HTMLElement>) {
    if (wasDrag()) return
    setAnchor(e.currentTarget)
  }

  return (
    <>
      <Tooltip title="Drag to resize · click for preset splits · arrow keys to nudge" enterDelay={600}>
        <Box
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panels — drag, click for preset splits, or use arrow keys"
          aria-valuenow={Math.round(valueNow)}
          aria-valuemin={Math.round(valueMin)}
          aria-valuemax={Math.round(valueMax)}
          tabIndex={0}
          onPointerDown={onPointerDown}
          onClick={onClick}
          onKeyDown={onKeyDown}
          sx={{
            // Wide, forgiving hit target; the visible grip is centered inside it.
            width: HANDLE_WIDTH, flexShrink: 0, cursor: 'col-resize', position: 'relative',
            display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center',
            touchAction: 'none', outline: 'none', zIndex: 1,
            '&:hover .grip, &:focus-visible .grip': { bgcolor: 'primary.main', opacity: 0.85, height: 56, width: 6 },
            '&:focus-visible': { boxShadow: (t) => `inset 0 0 0 2px ${t.palette.primary.main}` },
          }}
        >
          <Box
            className="grip"
            sx={{
              width: dragging ? 6 : 4, height: dragging ? 72 : 36, borderRadius: 2,
              bgcolor: dragging ? 'primary.main' : 'divider', opacity: dragging ? 1 : 0.9,
              transition: dragging ? 'none' : 'height .15s, width .15s, background-color .15s, opacity .15s',
            }}
          />
        </Box>
      </Tooltip>
      <Menu
        open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
        transformOrigin={{ vertical: 'center', horizontal: 'center' }}
      >
        <Typography variant="overline" color="text.secondary" sx={{ px: 2, display: 'block' }}>Split</Typography>
        {presets.map((p) => (
          <MenuItem
            key={p.label}
            selected={Math.abs(p.width - valueNow) < 2}
            onClick={() => { onPreset(p.width); setAnchor(null) }}
          >
            <ListItemText primary={p.label} secondary={`${p.width}px panel`} />
          </MenuItem>
        ))}
      </Menu>
      {dragging && (
        <Box
          sx={{
            position: 'fixed', inset: 0, cursor: 'col-resize',
            zIndex: (t) => t.zIndex.modal + 10, // above the surface iframe
          }}
        />
      )}
    </>
  )
}
